import { useEffect } from 'react';
import { useProjectQuery, useProjectDefinitionQuery } from 'src/app/projects/views/project-list/state/api/projectListGraphSlice';
import {
    getCurrentProjectId,
    getCurrentProjectSelectedHabitatAssayType,
} from 'src/app/projects/views/project-list/state/projectListSelector';
import { setCurrentProjectId as saveCurrentProjectId } from 'src/app/projects/views/project-list/state/projectListSlice';
import { useAppDispatch, useAppSelector } from 'src/store';
import { useSearchParams } from 'react-router-dom';
import useAuthService from './useAuthService';
import { Project, FeatureType } from '../types';
import { isBasicProject, hasHabitatInsightsFeature } from '../utils/featureHelpers';

const useProject = () => {
    const dispatch = useAppDispatch();
    const currentProjectId = useAppSelector(getCurrentProjectId);
    const [searchParams] = useSearchParams();
    const projectIdFromUrl = searchParams.get('projectId');
    const currentProjectSelectedHabitatAssayType = useAppSelector(getCurrentProjectSelectedHabitatAssayType);
    const { isAuthenticated } = useAuthService();

    const { currentData: projectDefinition } = useProjectDefinitionQuery(
        {
            projectId: currentProjectId || '',
        },
        {
            skip: !currentProjectId,
        }
    );

    useEffect(() => {
        if (projectIdFromUrl && projectIdFromUrl !== currentProjectId) {
            setCurrentProjectId(projectIdFromUrl);
        }
    }, [projectIdFromUrl, currentProjectId]);

    const setCurrentProjectId = (projectId: string | null) => {
        dispatch(saveCurrentProjectId(projectId));
    };

    const { currentData: currentProjectData, isFetching } = useProjectQuery(
        {
            projectId: currentProjectId || '',
        },
        {
            skip: !currentProjectId || !isAuthenticated,
        }
    );

    const { currentData: currentProjectDefinition } = useProjectDefinitionQuery(
        {
            projectId: projectIdFromUrl ?? '',
        },
        {
            skip: !projectIdFromUrl,
        }
    );

    const getCurrentProject = (): Project | null => {
        return currentProjectData?.project || null;
    };

    const getCurrentProjectDataAvailability = (): boolean => {
        if (!projectDefinition) {
            return false;
        }

        return projectDefinition?.samplesCount > 0;
    };

    /** Only true once definition is loaded — empty features while loading wrongly matched `isBasicProject([])`. */
    const isStarterProject = Boolean(projectDefinition && isBasicProject(projectDefinition.features));
    const isHabitatInsightsProject = hasHabitatInsightsFeature(projectDefinition?.features || []);
    const isEDnaProject = projectDefinition?.features.includes(FeatureType.EDNA) || false;

    return {
        currentProjectId,
        currentProjectDefinition,
        currentProjectDetails: getCurrentProject(),
        projectDefinition,
        isProjectAvailable: !!currentProjectId,
        isSurveyDesignAvailable: projectDefinition?.features.includes(FeatureType.HLCA) || false,
        isCurrentProjectEmpty: !getCurrentProjectDataAvailability(),
        isHabitatInsightsProject,
        isEDnaProject,
        isStarterProject,
        setCurrentProjectId,
        isFetching,
        isGRIISProject: Boolean(projectDefinition?.griisLookupCountries?.length),
        isSingleTimepoint: currentProjectSelectedHabitatAssayType?.singleTimepoint || false,
        isChegdProject: projectDefinition?.isChegdProject || false,
    };
};

export default useProject;
